// src/pages/Dashboard/Admin/Messages/components/MessagesUnifiedList.jsx

import React from "react";
import { FaComments, FaEye, FaTrash } from "react-icons/fa";

import {
  formatDateTimeFR,
  getInitials,
  getSourceMeta,
  getSubjectBadgeClass,
} from "../utils/messagesDashboardUtils";

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaComments />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun message à afficher
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Ajustez les filtres ou actualisez la liste.
        </p>
      </div>
    </section>
  );
}

function ActionButton({ title, icon, onClick, disabled, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    info: "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.base
      }`}
    >
      {icon}
    </button>
  );
}

export default function MessagesUnifiedList({
  messages,
  loading,
  deletingKey,
  onOpenDetails,
  onDelete,
}) {
  if (loading) return <SkeletonRows />;
  if (!messages.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Modération
          </p>

          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Flux des messages
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Les messages privés et les discussions de groupe sont réunis dans
            un seul flux, classé du plus récent au plus ancien.
          </p>
        </div>

        <div className="space-y-3">
          {messages.map((message) => {
            const sourceMeta = getSourceMeta(message.source);

            const subjectBadge =
              message.source === "private"
                ? getSubjectBadgeClass(message.subject)
                : sourceMeta.badge;

            return (
              <article
                key={message.key}
                className={`rounded-[24px] border bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-950 ${sourceMeta.row}`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex min-w-0 gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-black text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
                      {getInitials(message.senderName)}
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${sourceMeta.badge}`}
                        >
                          {sourceMeta.label}
                        </span>

                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${subjectBadge}`}
                          style={
                            message.source === "chat" && message.groupColor
                              ? {
                                  backgroundColor: message.groupColor,
                                  color: "#fff",
                                  borderColor: message.groupColor,
                                }
                              : undefined
                          }
                        >
                          {message.subject}
                        </span>

                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                          {formatDateTimeFR(message.date)}
                        </span>
                      </div>

                      <p className="mt-2 text-sm font-bold text-slate-900 dark:text-white">
                        {message.senderName}
                        <span className="mx-2 text-slate-400">→</span>
                        {message.source === "chat"
                          ? `Canal ${message.recipientName}`
                          : message.recipientName}
                      </p>

                      <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
                        {message.contentPreview || "—"}
                      </p>
                    </div>
                  </div>

                  <div className="flex shrink-0 justify-end gap-2">
                    <ActionButton
                      title="Voir le détail"
                      icon={<FaEye />}
                      onClick={() => onOpenDetails(message)}
                      tone="info"
                    />

                    <ActionButton
                      title="Supprimer"
                      icon={<FaTrash />}
                      onClick={() => onDelete(message)}
                      disabled={deletingKey === message.key}
                      tone="danger"
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}